import React, { useEffect, useState } from "react";

import { Autocomplete, Box, Button, Stack, TextField } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import StackedLineChartIcon from "@mui/icons-material/StackedLineChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  ResponsiveContainer,
} from "recharts";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import ApiDataTable from "./apiDataTable";
import useIsMobile from "./mobileview";

const ReChartClass = ({ apiData, index }) => {
  const [chartType, setChartType] = useState(null);
  const [chartData, setChartData] = useState(null);

  const [xAxisData, setxAxis] = useState(null);
  const [yAxisData, setyAxis] = useState(null);
  const [selectedXAxis, setSelectedXAxis] = React.useState<any>(null);
  const [selectedYAxis, setSelectedYAxis] = React.useState<any>(null);
  const isMobileView = useIsMobile();

  useEffect(() => {
    // set columns
    const columnsKeys = apiData.dataModel.map((obj) => obj.columnName);

    setxAxis(columnsKeys);
    setyAxis(columnsKeys);

    // set default X & Y Axis
    if (columnsKeys[0]) {
      setSelectedXAxis(columnsKeys[0]);
    }

    if (columnsKeys[1]) {
      setSelectedYAxis(columnsKeys[1]);
    }

    const arrayData = apiData.dataModel.map((obj) => obj.data);

    var cData = combineArraysIntoObjects(columnsKeys, arrayData);

    setChartData(cData);
  }, [apiData]);

  useEffect(() => {}, [selectedXAxis, selectedYAxis]);

  function combineArraysIntoObjects(keys, arrays) {
    if (!keys || keys.length === 0 || !arrays || arrays.length === 0) {
      return [];
    }

    const minLength = Math.min(...arrays.map((arr) => arr.length));

    return Array.from({ length: minLength }, (_, index) => {
      const obj = {};
      keys.forEach((key, keyIndex) => {
        obj[key] = arrays[keyIndex][index];
      });
      return obj;
    });
  }

  const renderChart = () => {
    switch (chartType) {
      case "line":
        return (
          <>
            <ResponsiveContainer width={"100%"} height={250}>
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={selectedXAxis} />
                <YAxis dataKey={selectedYAxis} />
                <Tooltip />
                <Legend />
                {yAxisData.map((row, rowIndex) => (
                  <Line type="monotone" dataKey={row} stroke="#96B7C0" />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </>
        );
      case "bar":
        return (
          <>
            <ResponsiveContainer width={"100%"} height={250}>
              <BarChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={selectedXAxis} />
                <YAxis dataKey={selectedYAxis} />
                <Tooltip />
                <Legend />
                <Bar dataKey={selectedYAxis} fill="#96B7C0" />
              </BarChart>
            </ResponsiveContainer>
          </>
        );
      case "datatable":
        return (
          <>
            <ApiDataTable
              apiData={apiData.dataModel}
              index={index}
            ></ApiDataTable>
          </>
        );
    }
  };

  return (
    <>
      {chartData && chartType && (
        <>
          <Box
            sx={{
              height: "30px",
              fontSize: "10px",
              width: "100%",
            }}
          >
            <IconButton
              sx={{
                float: "right",
                color: "darkgray",
                marginRight: "8px",
              }}
              size="small"
              onClick={() => setChartType(null)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              maxHeight: "500px",
              width: "100%",
              padding: "10px",
              fontSize: "12px",
              marginBottom: "10px",
              overflow: "auto",
            }}
          >
            {chartType !== "datatable" && (
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "start",
                  alignItems: "flex-end",
                  marginBottom: "20px",
                  marginTop: "10px",
                }}
              >
                <>
                  <Autocomplete
                    id="controlled-demo"
                    options={xAxisData}
                    value={selectedXAxis}
                    onChange={(event: any, newValue: any) => {
                      setSelectedXAxis(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="X-Axis" />
                    )}
                    size="small"
                    sx={{
                      width: "40%",
                      "& .MuiInputBase-root": {
                        height: "30px",
                        fontSize: "12px",
                      },
                    }}
                  />
                  <Autocomplete
                    id="controlled-demo"
                    options={yAxisData}
                    value={selectedYAxis}
                    onChange={(event: any, newValue: any) => {
                      setSelectedYAxis(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Y-Axis" />
                    )}
                    size="small"
                    sx={{
                      width: "40%",
                      "& .MuiInputBase-root": {
                        height: "30px",
                        fontSize: "12px",
                      },
                    }}
                  />
                </>
              </Stack>
            )}
            {renderChart()}
          </Box>
        </>
      )}

      <>
        <div
          style={{
            fontWeight: "bold",
          }}
        >
          Quick actions
        </div>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "start",
            alignItems: "flex-end",
            marginBottom: "10px",
          }}
        >
          {chartData && chartData.length > 1 && (
            <>
              <Button
                variant="outlined"
                startIcon={<BarChartIcon />}
                onClick={() => setChartType("bar")}
                size="small"
                sx={{
                  color: "#40616B",
                }}
              >
                {!isMobileView && "Bar chart"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<StackedLineChartIcon />}
                onClick={() => setChartType("line")}
                size="small"
                sx={{
                  color: "#40616B",
                }}
              >
                {!isMobileView && "Line chart"}
              </Button>
            </>
          )}

          <Button
            variant="outlined"
            startIcon={<TableChartIcon />}
            onClick={() => setChartType("datatable")}
            size="small"
            sx={{
              color: "#40616B",
            }}
          >
            {!isMobileView && "Data View"}
          </Button>
        </Stack>
      </>
    </>
  );
};

export default ReChartClass;
